// React and Router imports
import React from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';

// Redux imports
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

// Constants
import { FACULTY, STAFF } from '../constants/Constants';

// Route protection components
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

// Common components
import Layout from '../components/common/Layout';
import ScrollToTop from './common/ScrollToTop';

// Application pages/components
import ClaimProfileFlow from './ClaimProfileFlow';
import CreateIdea from './CreateIdea';
import Dashboard from './Dashboard';
import ForgotPassword from './ForgotPassword';
import Following from './Following';
import Home from './Home';
import IdeaDetails from './IdeaDetails';
import InviteOthers from './InviteOthers';
import MyIdeas from './MyIdeas';
import NotFound from './NotFound';
import PeopleFeed from './PeopleFeed';
import Profile from './Profile';
import RequestInvitation from './RequestInvitation';
import SignIn from './SignIn';
import TandCNotProtected from './TandCNotProtected';
import TandCProtected from './TandCProtected';
import UpdateIdea from './UpdateIdea';
import UpdateProfile from './UpdateProfile';

const AppRoutes: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const isAuthenticated = user.userDetails?.id ? true : false;

  const isFacultyOrStaff =
    user.userDetails?.userTypeId === FACULTY ||
    user.userDetails?.userTypeId === STAFF;

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* Public routes with common layout */}
          {!isAuthenticated && (
            <Route element={<PublicRoute isAuthenticated={isAuthenticated} />}>
              <Route index element={<Home />} />
              <Route path="signin" element={<SignIn />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="request-invite" element={<RequestInvitation />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}

          {/* Protected routes */}
          {isAuthenticated && (
            <Route
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  isAuthorized={true}
                />
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" index element={<Dashboard />} />
              <Route path="people" element={<PeopleFeed />} />
              <Route path="terms-and-conditions" element={<TandCProtected />} />
              <Route path="invite-others" element={<InviteOthers />} />
              <Route path="idea-creation" element={<CreateIdea />} />
              <Route path="update-profile" element={<UpdateProfile />} />
              <Route path="following" element={<Following />} />
              <Route path="ideas/:id" element={<IdeaDetails />} />
              <Route path="profile/:id" element={<Profile />} />
              <Route path="my-ideas" element={<MyIdeas />} />
              <Route path="my-ideas/edit/:id" element={<UpdateIdea />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          )}
        </Route>
        {/* Protected routes for Faculty or Staff only */}
        {isAuthenticated && isFacultyOrStaff && (
          <Route
            element={
              <PrivateRoute
                isAuthenticated={isAuthenticated}
                isAuthorized={isFacultyOrStaff}
              />
            }
          >
            <Route path="my-ideas" element={<MyIdeas />} />
            <Route path="idea-creation" element={<CreateIdea />} />
          </Route>
        )}
        {/* Public routes without common layout */}
        <Route path="accept-invitation" Component={ClaimProfileFlow} />
        <Route path="terms-conditions" Component={TandCNotProtected} />
        <Route
          path="*"
          element={<NotFound isAuthenticated={isAuthenticated} />}
        />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
